import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import MatchedProducts from "@/components/tools/MatchedProducts";
import HowWeCalculated from "@/components/tools/HowWeCalculated";
import EmailCapture from "@/components/tools/EmailCapture";
import PdfDownloadCard from "@/components/PdfDownloadCard";

const COMPLIANCE_LINE =
  "Estimates are for educational purposes only. Actual products, rates, and eligibility are subject to underwriting and carrier approval.";

/**
 * Shared results panel for all 6 calculators.
 * - headline: large editorial number/verdict
 * - subhead: optional supporting line
 * - rows: array of {label, value} for the breakdown grid
 * - assumptions: list of strings (collapsed under "How we calculated this")
 * - matchedIds / reasonsByProduct: used by <MatchedProducts>
 * - applyQuery: query string for the Talk-to-an-advisor CTA
 */
export const ResultsCard = ({
  headline,
  subhead,
  rows = [],
  assumptions = [],
  matchedIds = [],
  reasonsByProduct = {},
  products = [],
  applyQuery = "",
  source,
  toolSlug,
  toolInputs,
  toolResults,
}) => {
  const rootRef = useRef(null);

  useEffect(() => {
    // Smooth scroll the results into view when they appear.
    const t = setTimeout(() => {
      if (rootRef.current) {
        rootRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 80);
    return () => clearTimeout(t);
  }, []);

  const applyHref = `/apply${applyQuery ? `?${applyQuery}` : ""}`;

  return (
    <div
      ref={rootRef}
      data-testid="results-card"
      data-tool-source={source}
      className="reveal visible"
    >
      <div className="relative rounded-2xl border border-lw-gold/30 bg-white p-6 sm:p-9 shadow-[0_30px_80px_-40px_rgba(11,37,69,0.25)] overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(212,162,76,0.14) 0%, rgba(212,162,76,0) 70%)",
          }}
        />
        <div className="relative">
          <span className="lw-eyebrow">Your Estimate</span>
          <h2
            data-testid="results-headline"
            className="mt-2 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.05]"
          >
            {headline}
          </h2>
          {subhead && (
            <p
              data-testid="results-subhead"
              className="mt-3 max-w-2xl text-base sm:text-lg text-lw-muted leading-relaxed"
            >
              {subhead}
            </p>
          )}

          {rows.length > 0 && (
            <div
              data-testid="results-breakdown"
              className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 border-t border-lw-navy/10 pt-6"
            >
              {rows.map((r, i) => (
                <div
                  key={i}
                  data-testid={`results-row-${r.testId || i}`}
                  className="flex items-baseline justify-between gap-4"
                >
                  <span className="text-sm text-lw-muted">{r.label}</span>
                  <span className="font-serif-display text-lg text-lw-navy tabular-nums">
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-7">
            <HowWeCalculated assumptions={assumptions} />
          </div>

          {matchedIds.length > 0 && (
            <div className="mt-8">
              <MatchedProducts
                products={products}
                matchedIds={matchedIds}
                reasonsByProduct={reasonsByProduct}
              />
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={applyHref}
              data-testid="results-cta-apply"
              className="btn-gold"
            >
              Talk to an advisor about this <ArrowRight size={18} />
            </Link>
            <Link
              to="/tools"
              data-testid="results-cta-other-tools"
              className="btn-ghost-navy"
            >
              Try another tool
            </Link>
          </div>

          <p
            data-testid="results-compliance"
            className="mt-6 text-xs text-lw-muted leading-relaxed"
          >
            {COMPLIANCE_LINE}
          </p>

          <EmailCapture source={source} sourceContext={applyQuery ? Object.fromEntries(new URLSearchParams(applyQuery)) : {}} />

          {toolSlug && toolResults && (
            <div className="mt-6" data-testid={`pdf-report-section-${toolSlug}`}>
              <PdfDownloadCard
                endpoint={`/pdf/calculator/${toolSlug}`}
                filename={`lifespan-${toolSlug}-report.pdf`}
                title="Download your personalized report (PDF)"
                subtitle="A branded PDF with your inputs, results, recommended products, and next steps."
                extraBody={{ inputs: toolInputs || {}, results: toolResults }}
                testIdPrefix={`pdf-report-${toolSlug}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsCard;
